/** Admin notifications API client — thin fetch wrappers. */

export interface AdminNotification {
  id: string;
  event_type: string;
  severity: 'info' | 'warn' | 'error' | 'success';
  actor_sub: string | null;
  app_slug: string | null;
  title: string;
  body: string | null;
  payload: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
}

export interface AuditLogRow {
  id: string;
  app_id: string;
  app_slug: string | null;
  app_name: string | null;
  event_type: string;
  from_version: number | null;
  to_version: number | null;
  actor_sub: string;
  actor_email: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

/** List the most recent notifications for the admin bell + drawer. */
export async function fetchNotifications(token: string, limit = 50): Promise<AdminNotification[]> {
  const response = await fetch(`/api/admin/notifications?limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return [];
  const data = (await response.json()) as { items: AdminNotification[] };
  return data.items;
}

/** Unread count used by the bell badge. */
export async function fetchUnreadCount(token: string): Promise<number> {
  const response = await fetch('/api/admin/notifications/unread-count', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return 0;
  const data = (await response.json()) as { count: number };
  return data.count;
}

/** Mark every visible notification as read. */
export async function markAllRead(token: string): Promise<void> {
  await fetch('/api/admin/notifications/mark-all-read', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

/** Mark a single notification as read. */
export async function markOneRead(token: string, id: string): Promise<void> {
  await fetch(`/api/admin/notifications/${id}/read`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

/** Record an app-access role change into the audit feed. Best-effort. */
export async function recordAccessEvent(token: string, input: {
  action: 'grant' | 'revoke';
  appSlug: string;
  roleName: string;
  targetUserId: string;
  targetLabel?: string;
}): Promise<void> {
  await fetch('/api/admin/notifications/access-event', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/** Record a user-account mutation (admin flag, enabled flag, app-access diff)
 *  into the audit feed as one entry. Best-effort. */
export async function recordAuditEvent(token: string, input: {
  field: string;
  targetUserId: string;
  targetLabel?: string;
  summary: string;
  detail?: string;
  payload?: Record<string, unknown>;
}): Promise<void> {
  await fetch('/api/admin/notifications/audit-event', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/** Fetch the full audit log (last 200 events). */
export async function fetchAuditLog(token: string): Promise<AuditLogRow[]> {
  const response = await fetch('/api/admin/notifications/audit-log', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return [];
  const data = (await response.json()) as { items: AuditLogRow[] };
  return data.items;
}
